import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** No-op callback used until Angular Forms registers a handler. */
const noop = (): void => undefined;

/** Aero wrapper for PrimeNG input switch with Angular Forms support. */
@Component({
  selector: 'aero-toggle',
  templateUrl: './aero-toggle.component.html',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AeroToggleComponent),
    multi: true,
  }],
  standalone: false,
})
export class AeroToggleComponent implements ControlValueAccessor {
  /** DOM id assigned to the underlying PrimeNG input. */
  @Input() public inputId = '';

  /** Whether the toggle is disabled. */
  @Input() public disabled = false;

  /** Additional CSS class passed to the PrimeNG switch. */
  @Input() public styleClass = '';

  /** Tooltip text shown for the toggle. */
  @Input() public aeroTooltip = '';

  /** Whether the tooltip is disabled. */
  @Input() public tooltipDisabled = true;

  /** Accessible name for the toggle. */
  @Input() public ariaLabel = '';

  /** IDs of elements that label the toggle. */
  @Input() public ariaLabelledBy = '';

  /** Current checked state displayed by the PrimeNG switch. */
  public value = false;

  private onChange: (value: boolean) => void = noop;

  private onTouched: () => void = noop;

  /** Writes a form value into the toggle. */
  writeValue(value: boolean): void {
    this.value = !!value;
  }

  /** Registers the callback used to report value changes to Angular Forms. */
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  /** Registers the callback used to report that the toggle was touched. */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Updates the disabled state supplied by Angular Forms. */
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  /** Updates the checked state and reports it to Angular Forms. */
  handleChange(value: boolean): void {
    this.value = value;
    this.onChange(value);
  }

  /** Reports that the toggle lost focus. */
  handleBlur(): void {
    this.onTouched();
  }
}
